import React, { useCallback, useMemo, useState } from "react";

import RelationshipAccountsLoader from "./RelationshipAccountsLoader";
import SponsorStakeholderCard from "./SponsorStakeholderCard";
import Button from "../../Components/Button";
import SearchInput from "../../Components/Search/SearchInput";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  RelationshipAssessmentStakeholder,
  setSelectedStakeholderUserIds,
} from "../../Store/Reducers/RelationshipSlice";

interface SponsorStakeholderTabProps {
  stakeholders: RelationshipAssessmentStakeholder[];
  loading: boolean;
  error: string | null;
  loggedInUserId?: string;
  externalSelectedIds?: string[];
  onExternalSelectionChange?: (ids: string[]) => void;
  singleSelectMode?: boolean;
}

const stateMessageClassName =
  "flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500";

const SponsorStakeholderTab: React.FC<SponsorStakeholderTabProps> = ({
  stakeholders,
  loading,
  error,
  loggedInUserId,
  externalSelectedIds,
  onExternalSelectionChange,
  singleSelectMode = false,
}) => {
  const dispatch = useAppDispatch();
  const reduxSelectedIds = useAppSelector(
    state => state.relationship.selectedStakeholderUserIds
  );
  const [searchQuery, setSearchQuery] = useState("");

  const isControlled =
    externalSelectedIds !== undefined &&
    onExternalSelectionChange !== undefined;
  const activeSelectedIds = isControlled
    ? externalSelectedIds
    : reduxSelectedIds;

  const selectedIds = useMemo(
    () => new Set(activeSelectedIds),
    [activeSelectedIds]
  );

  const updateSelection = useCallback(
    (ids: string[]) => {
      if (isControlled) {
        onExternalSelectionChange!(ids);
      } else {
        dispatch(setSelectedStakeholderUserIds(ids));
      }
    },
    [isControlled, onExternalSelectionChange, dispatch]
  );

  const filteredStakeholders = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return stakeholders;

    return stakeholders.filter(stakeholder => {
      const searchable = [
        stakeholder.user_name,
        stakeholder.title,
        stakeholder.role,
        stakeholder.email,
        stakeholder.grade_name,
        stakeholder.label,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [searchQuery, stakeholders]);

  const selectStakeholder = useCallback(
    (userId: string) => {
      if (singleSelectMode) {
        updateSelection([userId]);
        return;
      }
      const isOnlySelected = selectedIds.size === 1 && selectedIds.has(userId);
      updateSelection(isOnlySelected ? [] : [userId]);
    },
    [selectedIds, updateSelection, singleSelectMode]
  );

  const selectAll = useCallback(() => {
    updateSelection(stakeholders.map(stakeholder => stakeholder.user_id));
  }, [stakeholders, updateSelection]);

  const clearAll = useCallback(() => {
    updateSelection(loggedInUserId ? [loggedInUserId] : []);
  }, [updateSelection, loggedInUserId]);

  const selectedCount = useMemo(
    () => stakeholders.filter(s => selectedIds.has(s.user_id)).length,
    [stakeholders, selectedIds]
  );

  const allSelected =
    stakeholders.length > 0 && selectedCount === stakeholders.length;
  const singleSelected = selectedCount === 1;

  if (loading) {
    return <RelationshipAccountsLoader label="Loading users…" />;
  }

  if (error) {
    return (
      <div className={`${stateMessageClassName} border-red-200 text-gray-600`}>
        <p className="text-base font-semibold text-gray-900">
          Unable to load users
        </p>
        <p className="mt-1 text-sm text-gray-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-1">
            <p className="m-0 text-sm font-medium text-gray-900">
              {allSelected
                ? `All ${stakeholders.length} users selected`
                : singleSelected
                  ? "1 user selected"
                  : `${selectedCount} of ${stakeholders.length} users selected`}
            </p>
            <p className="m-0 text-xs text-gray-500">
              {singleSelectMode
                ? "Select a user to view their relationships"
                : "Select one user or use Select All"}
              {searchQuery
                ? ` · Showing ${filteredStakeholders.length} result${filteredStakeholders.length === 1 ? "" : "s"} for "${searchQuery.trim()}"`
                : ` · Showing ${filteredStakeholders.length} result${filteredStakeholders.length === 1 ? "" : "s"}`}
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {!singleSelectMode ? (
              <div className="flex items-center gap-2">
                <Button
                  variant={allSelected ? "primary" : "white"}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium"
                  onClick={selectAll}
                  disabled={allSelected}
                >
                  Select All
                </Button>
                <Button
                  variant="white"
                  className="rounded-lg px-3 py-1.5 text-xs font-medium"
                  onClick={clearAll}
                  disabled={
                    selectedCount === 0 ||
                    (loggedInUserId !== undefined &&
                      selectedCount === 1 &&
                      selectedIds.has(loggedInUserId))
                  }
                >
                  Clear All
                </Button>
              </div>
            ) : null}
            <div className="w-full sm:w-72">
              <SearchInput
                placeholder="Search users..."
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {filteredStakeholders.length === 0 ? (
        <div className={stateMessageClassName}>No users found.</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
          {filteredStakeholders.map(stakeholder => (
            <SponsorStakeholderCard
              key={stakeholder.user_id}
              stakeholder={stakeholder}
              isSelected={selectedIds.has(stakeholder.user_id)}
              onToggle={() => selectStakeholder(stakeholder.user_id)}
              isCurrentUser={
                !!loggedInUserId && stakeholder.user_id === loggedInUserId
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SponsorStakeholderTab;
